import {
	type SustainabilityFormDto,
	SustainabilityFormStatus,
	type SustainabilityQuestionDto,
	type SustainabilitySectionDto,
	SustainabilitySections
} from '../../models/sustainability';
import type { ProfileCertificationDto } from '../../models/certification';
import { selectProfileSectionsByProfileType } from './profile-sections.slice';
import { profileCertificationsCacheKey } from './profile-certifications.slice';

const CERTIFICATIONS_SECTION_NAME =
	SustainabilitySections[SustainabilitySections.Certifications];

const JOURNEY_ANSWERS_REQUIRING_CERTIFICATION = new Set([
	'certified (3rd party audit)',
	'certified (third-party audit)',
	'in the process of getting certified'
]);

export type SustainabilityFormsState = {
	sustainabilityForms: {
		profileForms: Record<number, Record<number, SustainabilityFormDto>>;
	};
};

type StateWithFormsAndProfileSections = SustainabilityFormsState & {
	profileSections?: { sectionsByProfileType?: Record<number, SustainabilitySectionDto[]> };
};

type StateWithFormsAndQuestions = SustainabilityFormsState & {
	sustainabilityQuestions: {
		data: Record<number, Record<string, SustainabilityQuestionDto[]>>;
	};
};

type StateWithSectionStatusInputs = StateWithFormsAndQuestions & {
	profileCertifications?: {
		profileCertificationsByKey?: Record<string, ProfileCertificationDto[] | null>;
	};
};

const getProfileCertifications = (
	state: StateWithSectionStatusInputs,
	profileId: number,
	formId: number
): ProfileCertificationDto[] => {
	return (
		state.profileCertifications?.profileCertificationsByKey?.[
			profileCertificationsCacheKey(profileId, formId)
		] ?? []
	);
};

export const selectAnswersByFormId = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number
): Record<number, string> => {
	const form = state.sustainabilityForms?.profileForms?.[profileId]?.[formId];
	const answers = form?.answers ?? [];
	return answers.reduce<Record<number, string>>((acc, a) => {
		acc[a.sustainabilityQuestionId] = a.answer;
		return acc;
	}, {});
};

export const selectAnswerByQuestionId = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number,
	questionId: number
): string | undefined => {
	return state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.answers?.find(
		(a) => a.sustainabilityQuestionId === questionId
	)?.answer;
};

export const selectFormByFormId = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number
): SustainabilityFormDto | undefined => {
	return state.sustainabilityForms?.profileForms?.[profileId]?.[formId];
};

export const selectAnswerSections = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number
) => state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.answerSections ?? [];

export const selectLatestInProgressFormByProfileId = (
	state: SustainabilityFormsState,
	profileId: number
): SustainabilityFormDto | undefined => {
	const byFormId = state.sustainabilityForms?.profileForms?.[profileId];
	if (!byFormId) return undefined;
	return Object.values(byFormId).find(
		(form) =>
			form.status === SustainabilityFormStatus.InProgress ||
			form.status === SustainabilityFormStatus.WaitingForApproval
	);
};

export const selectAnswersBySection = (
	state: StateWithFormsAndQuestions,
	profileId: number,
	formId: number,
	profileType: number,
	section: string
): Record<number, string> => {
	const form = state.sustainabilityForms?.profileForms?.[profileId]?.[formId];
	const formAnswers = form?.answers ?? [];
	const questionsInSection = state.sustainabilityQuestions?.data?.[profileType]?.[section] ?? [];
	const questionIdsInSection = new Set(questionsInSection.map((q) => q.id));
	return formAnswers
		.filter((a) => questionIdsInSection.has(a.sustainabilityQuestionId))
		.reduce<Record<number, string>>((acc, a) => {
			acc[a.sustainabilityQuestionId] = a.answer;
			return acc;
		}, {});
};

export type SectionStatus = 'NotStarted' | 'InProgress' | 'Completed';

function toSectionStatus(status: string | undefined): SectionStatus {
	if (status === 'NotStarted' || status === 'InProgress' || status === 'Completed') {
		return status;
	}
	return 'NotStarted';
}

export const selectAppliciableSection = (
	state: SustainabilityFormsState,
	profileId: number,
	formId: number,
	section: string
): boolean => {
	const answerSections = selectAnswerSections(state, profileId, formId);
	const answerSection = answerSections.find((s) => s.section === section);

	if (answerSection?.isApplicable === null || answerSection?.isApplicable === undefined)
		return true;

	return answerSection?.isApplicable ?? true;
};

const hasProfileCertifications = (
	state: StateWithSectionStatusInputs,
	profileId: number,
	formId: number
): boolean => getProfileCertifications(state, profileId, formId).length > 0;

const hasCompletedProfileCertifications = (
	state: StateWithSectionStatusInputs,
	profileId: number,
	formId: number
): boolean => {
	const certifications = getProfileCertifications(state, profileId, formId);
	if (certifications.length === 0) {
		return false;
	}

	return certifications.every((certification) => {
		const hasCertificationName = certification.certificationName.trim().length > 0;
		const hasJourneyAnswer = certification.journeyAnswer.trim().length > 0;
		const hasSupportingDocument =
			(certification.document?.documentUrl?.trim().length ?? 0) > 0;

		return hasCertificationName && hasJourneyAnswer && hasSupportingDocument;
	});
};

const selectedJourneyRequiringCertificationWithoutCertification = (
	state: StateWithSectionStatusInputs,
	profileId: number,
	formId: number,
	profileType: number
): boolean => {
	if (hasCompletedProfileCertifications(state, profileId, formId)) {
		return false;
	}

	const certificationQuestions =
		state.sustainabilityQuestions?.data?.[profileType]?.[CERTIFICATIONS_SECTION_NAME] ?? [];

	for (const question of certificationQuestions) {
		const answer = selectAnswerByQuestionId(state, profileId, formId, question.id);
		if (!answer) {
			continue;
		}

		const normalizedAnswer = answer.trim().toLowerCase();
		const selectedOption = question.options.find((option) => String(option.id) === answer);

		if (
			(selectedOption &&
				JOURNEY_ANSWERS_REQUIRING_CERTIFICATION.has(
					selectedOption.text.trim().toLowerCase()
				)) ||
			JOURNEY_ANSWERS_REQUIRING_CERTIFICATION.has(normalizedAnswer)
		) {
			return true;
		}
	}

	return false;
};

export const selectSectionStatus = (
	state: StateWithSectionStatusInputs,
	profileId: number,
	formId: number,
	profileType: number,
	section: string
): SectionStatus => {
	if (
		section === CERTIFICATIONS_SECTION_NAME &&
		hasCompletedProfileCertifications(state, profileId, formId)
	) {
		return 'Completed';
	}

	if (
		section === CERTIFICATIONS_SECTION_NAME &&
		hasProfileCertifications(state, profileId, formId) &&
		!hasCompletedProfileCertifications(state, profileId, formId)
	) {
		return 'InProgress';
	}

 if (
 	section === CERTIFICATIONS_SECTION_NAME &&
		selectedJourneyRequiringCertificationWithoutCertification(
			state,
			profileId,
			formId,
			profileType
		)
 ) {
	return 'InProgress';
 }

	const questionsInSection = state.sustainabilityQuestions?.data?.[profileType]?.[section] ?? [];
	const questionCount = questionsInSection.length;

	if (questionCount === 0) {
		const answerSection = selectAnswerSections(state, profileId, formId).find(
			(s) => s.section === section
		);
		return toSectionStatus(answerSection?.status);
	}

	const answered = selectAnswersBySection(state, profileId, formId, profileType, section);
	const answeredCount = Object.keys(answered).length;

	if (answeredCount === 0) return 'NotStarted';
	if (answeredCount > 0 && answeredCount < questionCount) return 'InProgress';
	return 'Completed';
};

export const selectCanSubmitFormForApproval = (
	state: StateWithFormsAndProfileSections & StateWithSectionStatusInputs,
	profileId: number,
	formId: number,
	profileType: number
): boolean => {
	const form = selectFormByFormId(state, profileId, formId);
	if (form?.status !== SustainabilityFormStatus.InProgress) return false;

	const formSections = selectProfileSectionsByProfileType(state, profileType);

	const sectionsRequiringCompletion = formSections.filter((formSection) => {
		if (formSection.hasApplicableCheckbox) {
			return (
				form?.answerSections?.find((section) => section.section === formSection.name)
					?.isApplicable ?? true
			);
		}
		return true;
	});

	if (sectionsRequiringCompletion.length === 0) return false;

	return sectionsRequiringCompletion.every(
		(formSection) =>
			selectSectionStatus(state, profileId, formId, profileType, formSection.name) === 'Completed'
	);
};

export const selectIsFormInProgress = (
	state: StateWithFormsAndProfileSections,
	profileId: number,
	formId: number
): boolean => {
	return (
		state.sustainabilityForms?.profileForms?.[profileId]?.[formId]?.status ===
		SustainabilityFormStatus.InProgress
	);
};
